import React from 'react';
import { Link } from 'react-router-dom';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';
import CTA from '../components/CTA';

const PATIO_FAQ = [
  {
    question: 'How often should I have my patio cleaned?',
    answer: 'We recommend cleaning your patio at least once a year to maintain its appearance and prevent buildup of dirt, mold, and algae. In areas with heavy use or high humidity, more frequent cleaning may be necessary to keep your outdoor space looking its best.'
  },
  {
    question: 'Is your cleaning process safe for all patio materials?',
    answer: {
      intro: 'Yes, we use methods and solutions that are safe for all common patio materials:',
      list: [
        'Concrete patios',
        'Pavers and stone',
        'Brick surfaces',
        'Wood decking',
        'Composite materials'
      ],
      outro: 'Our technicians will assess your specific surface and use the appropriate cleaning method to ensure optimal results without damage.'
    }
  },
  {
    question: 'Can you remove tough stains from my patio?',
    answer: 'Absolutely! Our cleaning process is designed to remove various types of stains including rust, oil, algae, and general dirt buildup. We use specialized solutions and techniques to effectively clean your patio while preserving its appearance and integrity.'
  }
];

const PatioCleaning = () => {
  const serviceSEO = {
    title: 'Patio Cleaning Services in Phoenix | Copper Canyon Pressure Wash',
    description: 'Professional patio cleaning in Phoenix, AZ. Remove dirt, mold, and stains. Expert technicians, eco-friendly solutions. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Patio Cleaning',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Patio Cleaning'
        }
      }
    }
  };
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Patio Cleaning Services | Copper Canyon Pressure Wash"
        description="Professional patio cleaning in Phoenix, AZ. Remove stains & restore beauty. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="concrete patio cleaning, paver patio cleaning, stone patio cleaning, Phoenix patio maintenance, patio stain removal, outdoor living space cleaning, patio sealing preparation, patio power washing, patio surface restoration, patio safety cleaning"
      />
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Service Banner">
        <picture className="w-full h-full">
          <source srcSet="/patio.avif" type="image/avif" />
          <source srcSet="/patio.webp" type="image/webp" />
          <img 
            src="/patio.JPG" 
            alt="Professional patio cleaning service in action" 
            className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
          />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Patio Cleaning</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Patio Cleaning Services</h2>
        <p className="mb-6 text-gray-700">
          At Copper Canyon Pressure Wash, we provide professional patio cleaning services for homeowners in the greater Phoenix area, based out of Scottsdale. Patios are often the heart of outdoor living, but they can accumulate dirt, algae, and stains from Arizona's harsh desert climate and frequent use. Our team uses specialized pressure washing techniques to revive your patio, making it a clean, inviting space for relaxation and gatherings.
        </p>
        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Slip-Free Surfaces</h3>
            </div>
            <p className="text-gray-700">We eliminate slippery algae and moss that can build up on patios, ensuring a safer surface for your family and guests.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Color & Texture Restoration</h3>
            </div>
            <p className="text-gray-700">Our cleaning methods restore the original color and texture of your patio, whether it's made of concrete, flagstone, or brick.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Eco-Friendly Stain Removal</h3>
            </div>
            <p className="text-gray-700">We use eco-friendly detergents to remove stains like spilled food or rust, without harming nearby plants or grass.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Outdoor Enjoyment</h3>
            </div>
            <p className="text-gray-700">A clean patio enhances your outdoor space, making it the perfect spot for entertaining or unwinding in the Arizona sun.</p>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Patio Cleaning Process</h2>
        <div className="bg-[#f8f7f3] rounded-lg p-6 mb-8">
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Inspection</h3>
                <p className="text-gray-700">We start by inspecting your patio for dirt, algae, or stains from things like BBQ grease or desert dust.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Biodegradable Pre-Treatment</h3>
                <p className="text-gray-700">We apply biodegradable detergents to loosen grime and treat organic growth, keeping your outdoor area safe and clean.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Gentle or Strong Cleaning</h3>
                <p className="text-gray-700">Using adjustable pressure washers, we clean with a gentle setting for delicate materials like flagstone, or a stronger setting for concrete, as needed.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Final Rinse & Inspection</h3>
                <p className="text-gray-700">We finish with a thorough rinse and inspection, ensuring your patio looks refreshed and ready for your next outdoor event.</p>
              </div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Expertise in Patio Cleaning</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Surface-Specific Pressure</h3>
            <p className="text-gray-700">We select the right pressure—like 1,000 PSI for natural stone or 2,000 PSI for concrete—to prevent damage like chipping or etching.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">All Patio Materials</h3>
            <p className="text-gray-700">We're experienced with all patio materials, from porous limestone to sealed pavers, ensuring each surface gets the proper care.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Damage Prevention</h3>
            <p className="text-gray-700">Our expertise avoids issues like eroded joints or faded finishes, which can occur when inexperienced cleaners use incorrect methods.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Arizona Patio Know-How</h3>
            <p className="text-gray-700">We understand how Arizona's climate affects patios, delivering a clean that's both effective and safe for your outdoor space.</p>
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>
      <FAQ items={PATIO_FAQ} title="Frequently Asked Questions About Patio Cleaning" />
      <Link 
        to="/contact" 
        className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
      >
        Get a Free Quote
      </Link>
    </div>
  );
};

export default PatioCleaning; 